import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Icon, IconName} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {flexCenter} from '../../../../../../libs/shared/ui/src/styles/mixins';

/*
 * Props.
 */

export interface ActivityPillProps {
  isOpen?: boolean;
  isSquared?: boolean;
  icon?: IconName;

  children: ReactNode;
}

/*
 * Styles.
 */

export const StyledPillDiv = styled.div<{$isSquared: boolean; $isOpen: boolean}>`
  ${flexCenter};
  gap: 4px;

  height: 16px;
  width: fit-content;
  max-width: 250px;

  border-radius: ${(p) => (p.$isSquared ? '2px' : '16px')};
  padding: 0 4px;

  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.tiny};
  font-weight: ${fontWeights.semibold};

  background: ${(p) => (p.$isOpen ? p.theme.greys.shade30 : p.theme.greys.shade10)};
  border: 1px solid ${(p) => p.theme.greys.shade60};

  &:hover {
    background: ${(p) => p.theme.greys.shade30};
  }
`;

const StyledTooltipContentDiv = styled.div`
  ${flexCenter};
  gap: 4px;
`;

const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  width: fit-content;
`;

/*
 * Component.
 */

/** Generic activity pill component. */
export const ActivityPill: FC<ActivityPillProps> = ({icon, children, isOpen = false, isSquared = false}) => (
  <StyledTooltipCoordinator
    render={() => (
      <StyledTooltipContentDiv>
        {icon && <Icon name={icon} />}
        {children}
      </StyledTooltipContentDiv>
    )}
  >
    <StyledPillDiv $isOpen={isOpen} $isSquared={isSquared}>
      {icon && <Icon name={icon} />}
      <TooltipOverflowCondition>{children}</TooltipOverflowCondition>
    </StyledPillDiv>
  </StyledTooltipCoordinator>
);
